export const STUDENT_TRACKING_PAGE_SIZE = 10;

/**
 * @typedef {{ bloque?: string, tema?: string }} StudentTrackingTopic
 * @typedef {{ visto?: boolean }} StudentTrackingContent
 * @typedef {{
 *   contenidosVisualizados?: number,
 *   totalContenidos?: number,
 *   contenidos?: StudentTrackingContent[],
 * }} StudentTrackingProgressContent
 * @typedef {{
 *   usuarioId: string,
 *   nombre?: string,
 *   email?: string,
 *   resumenPorTema?: StudentTrackingTopic[],
 *   progresoContenido?: StudentTrackingProgressContent,
 * }} StudentTrackingAlumno
 * @typedef {{
 *   query: string,
 *   topicKey: string,
 *   evaluatedContents: boolean,
 *   unevaluatedContents: boolean,
 * }} StudentTrackingFilters
 * @typedef {{ value: string, label: string }} StudentTrackingTopicOption
 */

/**
 * @param {StudentTrackingTopic | null | undefined} tema
 * @returns {string}
 */
export function getStudentTrackingTopicKey(tema) {
  const bloque = String(tema?.bloque || "").trim();
  const nombre = String(tema?.tema || "").trim();
  if (!bloque && !nombre) return "";
  return `${bloque}|${nombre}`;
}

/**
 * @param {StudentTrackingTopic | null | undefined} tema
 * @returns {string}
 */
export function getStudentTrackingTopicLabel(tema) {
  const bloque = String(tema?.bloque || "").trim();
  const nombre = String(tema?.tema || "").trim();
  if (bloque && nombre) return `${bloque} → ${nombre}`;
  return nombre || bloque;
}

/**
 * @param {StudentTrackingAlumno[] | null | undefined} alumnos
 * @returns {StudentTrackingTopicOption[]}
 */
export function collectStudentTrackingTopics(alumnos) {
  const topics = new Map();
  (Array.isArray(alumnos) ? alumnos : []).forEach((alumno) => {
    (alumno.resumenPorTema || []).forEach((tema) => {
      const value = getStudentTrackingTopicKey(tema);
      if (!value || topics.has(value)) return;
      topics.set(value, {
        value,
        label: getStudentTrackingTopicLabel(tema),
      });
    });
  });
  return [...topics.values()].sort((left, right) => left.label.localeCompare(right.label, "es"));
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizeSearchText(value) {
  return String(value || "").trim().toLocaleLowerCase("es");
}

/**
 * @param {StudentTrackingAlumno} alumno
 * @returns {{ viewed: number, total: number, pending: number }}
 */
export function getStudentContentCounts(alumno) {
  const progress = alumno.progresoContenido || {};
  const contents = Array.isArray(progress.contenidos) ? progress.contenidos : null;
  if (contents) {
    const viewed = contents.filter((item) => Boolean(item.visto)).length;
    const total = contents.length;
    return { viewed, total, pending: Math.max(0, total - viewed) };
  }
  const viewed = Number(progress.contenidosVisualizados) || 0;
  const total = Number(progress.totalContenidos) || 0;
  return { viewed, total, pending: Math.max(0, total - viewed) };
}

/**
 * @param {StudentTrackingFilters} filters
 * @returns {boolean}
 */
export function hasActiveStudentTrackingFilters(filters) {
  return Boolean(
    String(filters.query || "").trim()
    || String(filters.topicKey || "").trim()
    || filters.evaluatedContents
    || filters.unevaluatedContents,
  );
}

/**
 * @param {StudentTrackingAlumno} alumno
 * @param {StudentTrackingFilters} filters
 * @returns {boolean}
 */
export function studentMatchesTrackingFilters(alumno, filters) {
  const query = normalizeSearchText(filters.query);
  if (query) {
    const haystack = `${normalizeSearchText(alumno.nombre)} ${normalizeSearchText(alumno.email)}`;
    if (!haystack.includes(query)) return false;
  }

  const topicKey = String(filters.topicKey || "").trim();
  if (topicKey) {
    const hasTopic = (alumno.resumenPorTema || []).some(
      (tema) => getStudentTrackingTopicKey(tema) === topicKey,
    );
    if (!hasTopic) return false;
  }

  if (filters.evaluatedContents || filters.unevaluatedContents) {
    const counts = getStudentContentCounts(alumno);
    if (filters.evaluatedContents && counts.viewed <= 0) return false;
    if (filters.unevaluatedContents && counts.pending <= 0) return false;
  }

  return true;
}

/**
 * @param {StudentTrackingAlumno[] | null | undefined} alumnos
 * @param {StudentTrackingFilters} filters
 * @returns {StudentTrackingAlumno[]}
 */
export function filterStudentTrackingList(alumnos, filters) {
  const list = Array.isArray(alumnos) ? alumnos : [];
  if (!hasActiveStudentTrackingFilters(filters)) return list;
  return list.filter((alumno) => studentMatchesTrackingFilters(alumno, filters));
}

/**
 * @param {StudentTrackingAlumno[]} alumnos
 * @param {number} page
 * @param {number} [pageSize]
 * @returns {{
 *   items: StudentTrackingAlumno[],
 *   page: number,
 *   totalPages: number,
 *   total: number,
 *   pageSize: number,
 *   from: number,
 *   to: number,
 * }}
 */
export function paginateStudentTrackingList(
  alumnos,
  page,
  pageSize = STUDENT_TRACKING_PAGE_SIZE,
) {
  const list = Array.isArray(alumnos) ? alumnos : [];
  const size = pageSize > 0 ? pageSize : STUDENT_TRACKING_PAGE_SIZE;
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / size) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * size;
  const items = list.slice(start, start + size);
  return {
    items,
    page: safePage,
    totalPages,
    total,
    pageSize: size,
    from: total === 0 ? 0 : start + 1,
    to: start + items.length,
  };
}
